import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { API_BASE, loginUrl, setUnauthorizedHandler } from './api/client';
import { useMe } from './api/hooks';
import { queryClient } from './queryClient';
import { usePlayer } from './player/store';
import { isEmbedded } from './ui/store';
import { BottomNav, Sidebar, TopBar } from './components/Shell';
import { PlayerBar } from './components/PlayerBar';
import { NowPlaying } from './components/NowPlaying';
import { ContextMenu, DeleteDialog, PlaylistPicker, Toasts } from './components/Overlays';
import { LogoMark, Wordmark } from './components/Wordmark';
import { Loading } from './components/States';
import { HomePage } from './pages/Home';
import { SearchPage } from './pages/Search';
import { AlbumsPage, ArtistsPage, ComposersPage, FoldersPage, GenresPage, LibraryPage, TracksPage } from './pages/Library';
import { AlbumPage, ArtistPage, ComposerPage, GenrePage, PlaylistPage } from './pages/Detail';
import { FavoritesPage, HistoryPage, PlaylistsPage, SettingsPage } from './pages/Personal';
import { SeriesPage, StoriesPage } from './pages/Stories';

/* Pages d'outils (envoi, édition, rapport, règles) : chargées à part, pour un démarrage plus léger */
const loadUploads = () => import('./pages/Uploads');
const loadEdit = () => import('./pages/Edit');
const loadEditBulk = () => import('./pages/EditBulk');
const loadSmart = () => import('./pages/Smart');
const loadReport = () => import('./pages/Report');
const UploadsPage = lazy(() => loadUploads().then((m) => ({ default: m.UploadsPage })));
const EditHomePage = lazy(() => loadEdit().then((m) => ({ default: m.EditHomePage })));
const EditPage = lazy(() => loadEdit().then((m) => ({ default: m.EditPage })));
const EditBulkPage = lazy(() => loadEditBulk().then((m) => ({ default: m.EditBulkPage })));
const SmartEditorPage = lazy(() => loadSmart().then((m) => ({ default: m.SmartEditorPage })));
const ReportPage = lazy(() => loadReport().then((m) => ({ default: m.ReportPage })));
/** Préchargées quand le navigateur n'a rien d'autre à faire : l'ouverture reste instantanée */
function prefetchTools() {
  const run = () => [loadUploads, loadEdit, loadEditBulk, loadSmart, loadReport].forEach((l) => void l().catch(() => undefined));
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  window.setTimeout(() => (w.requestIdleCallback ? w.requestIdleCallback(run, { timeout: 10_000 }) : run()), 4000);
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthGate>
          <Layout />
        </AuthGate>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/**
 * Exige une session Keycloak. Hors iframe : redirection vers /auth/login.
 * Dans l'intranet (iframe) : Keycloak refuse d'être affiché en cadre,
 * on ouvre donc la connexion dans une fenêtre surgissante.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const [needLogin, setNeedLogin] = useState(false);

  useEffect(() => {
    setUnauthorizedHandler(() => setNeedLogin(true));
    const onMsg = (e: MessageEvent) => {
      if (e.origin === location.origin && e.data?.type === 'pujolisique:login') {
        setNeedLogin(false);
        queryClient.invalidateQueries();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const unauth = needLogin || (me.error as { status?: number } | null)?.status === 401;

  useEffect(() => {
    if (unauth && !isEmbedded && !API_BASE) location.href = loginUrl();
  }, [unauth]);

  if (unauth) {
    return (
      <Gate>
        {isEmbedded ? (
          <>
            <p>Connectez-vous avec votre compte Pujolinet pour écouter la musique.</p>
            <button className="btn btn--gold" onClick={() => window.open(loginUrl('/', true), 'pujolisique-login', 'width=480,height=680')}>
              Se connecter
            </button>
          </>
        ) : (
          <p>Redirection vers la connexion…</p>
        )}
      </Gate>
    );
  }
  if (me.isLoading)
    return (
      <Gate>
        <Loading />
      </Gate>
    );
  if (me.error)
    return (
      <Gate>
        <p>{(me.error as Error).message}</p>
        <button className="btn" onClick={() => me.refetch()}>
          Réessayer
        </button>
      </Gate>
    );
  return <>{children}</>;
}

/** Écran de connexion et de chargement : le logo sur le tableau */
function Gate({ children }: { children: React.ReactNode }) {
  return (
    <div className="gate">
      <div className="gate__card">
        <LogoMark />
        <Wordmark />
        {children}
      </div>
      <p className="gate__credit">Frank Dicksee, La Belle Dame sans Merci (1902)</p>
    </div>
  );
}

function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('input,textarea,select,[contenteditable]') || e.ctrlKey || e.metaKey || e.altKey) return;
      const p = usePlayer.getState();
      switch (e.key) {
        case ' ':
          if (el.closest('button,a')) return;
          e.preventDefault();
          p.toggle();
          break;
        case 'ArrowRight':
          if (el.closest('[role=slider],input[type=range]')) return;
          p.seek(p.currentTime + (e.shiftKey ? 30 : 10));
          break;
        case 'ArrowLeft':
          if (el.closest('[role=slider],input[type=range]')) return;
          p.seek(p.currentTime - (e.shiftKey ? 30 : 10));
          break;
        case 'ArrowUp':
          if (el.closest('.menu')) return;
          e.preventDefault();
          p.setVolume(p.volume + 0.05);
          break;
        case 'ArrowDown':
          if (el.closest('.menu')) return;
          e.preventDefault();
          p.setVolume(p.volume - 0.05);
          break;
        case 'n':
        case 'N':
          p.next();
          break;
        case 'p':
        case 'P':
          p.prev();
          break;
        case 'm':
        case 'M':
          p.toggleMute();
          break;
        case 'l':
        case 'L':
          p.openNowPlaying(!(p.nowPlayingOpen && p.panelTab === 'lyrics'), 'lyrics');
          break;
        case 'q':
        case 'Q':
          p.openNowPlaying(!(p.nowPlayingOpen && p.panelTab === 'queue'), 'queue');
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

function Layout() {
  const loc = useLocation();
  const main = useRef<HTMLElement>(null);
  const npOpen = usePlayer((s) => s.nowPlayingOpen);
  const close = usePlayer((s) => s.openNowPlaying);
  useShortcuts();
  useEffect(prefetchTools, []);

  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
    close(false);
  }, [loc.pathname, close]);

  return (
    <>
      <div className="backdrop" aria-hidden />
      <div className={`app ${isEmbedded ? 'app--embed' : ''} ${npOpen ? 'np-open' : ''}`}>
        <a href="#main" className="skip">
          Aller au contenu
        </a>
        <Sidebar />
        <main
          className="main"
          id="main"
          ref={main}
          tabIndex={-1}
          onScroll={(e) => e.currentTarget.classList.toggle('is-scrolled', e.currentTarget.scrollTop > 8)}
        >
          <TopBar />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/albums" element={<AlbumsPage />} />
              <Route path="/tracks" element={<TracksPage />} />
              <Route path="/album/:id" element={<AlbumPage />} />
              <Route path="/artists" element={<ArtistsPage />} />
              <Route path="/artist/:id" element={<ArtistPage />} />
              <Route path="/composers" element={<ComposersPage />} />
              <Route path="/composer/:id" element={<ComposerPage />} />
              <Route path="/genres" element={<GenresPage />} />
              <Route path="/genre/:id" element={<GenrePage />} />
              <Route path="/folders" element={<FoldersPage />} />
              <Route path="/folders/:id" element={<FoldersPage />} />
              <Route path="/playlists" element={<PlaylistsPage />} />
              <Route path="/playlist/:id" element={<PlaylistPage />} />
              <Route path="/playlists/smart/new" element={<SmartEditorPage />} />
              <Route path="/playlist/:id/rules" element={<SmartEditorPage />} />
              <Route path="/report" element={<ReportPage />} />
              <Route path="/favorites" element={<FavoritesPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/stories" element={<StoriesPage />} />
              <Route path="/stories/:id" element={<SeriesPage />} />
              <Route path="/uploads" element={<UploadsPage />} />
              <Route path="/edit" element={<EditHomePage />} />
              <Route path="/edit/bulk" element={<EditBulkPage />} />
              <Route path="/edit/:kind/:id" element={<EditPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <PlayerBar />
        <BottomNav />
        <NowPlaying />
        <ContextMenu />
        <PlaylistPicker />
        <DeleteDialog />
        <Toasts />
      </div>
    </>
  );
}
